
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { CreditCard, Plus, CheckCircle, XCircle, Loader2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { useQuery } from "@tanstack/react-query";

const SellerPayment = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(false);

  const { data: account, isLoading: isLoadingAccount } = useQuery({
    queryKey: ['stripe-account'],
    queryFn: async () => {
      console.log('Fetching stripe account...');
      const { data, error } = await supabase
        .from('stripe_accounts')
        .select('*')
        .maybeSingle();

      if (error) {
        console.error('Error fetching stripe account:', error);
        throw error;
      }
      console.log('Stripe account data:', data);
      return data;
    }
  });

  const handleConnectStripe = async () => {
    try {
      setIsLoading(true);
      console.log('Getting session...');
      const { data: { session } } = await supabase.auth.getSession();
      
      if (!session) {
        console.error('No session found');
        throw new Error('Not authenticated');
      }

      console.log('Calling create-connect-account function...');
      const response = await supabase.functions.invoke('create-connect-account', {
        headers: {
          Authorization: `Bearer ${session.access_token}`
        }
      });
      
      console.log('Function response:', response);
      
      if (response.error) throw response.error;
      if (response.data?.url) window.location.href = response.data.url;

    } catch (error: any) {
      console.error('Error connecting Stripe:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to connect payment account. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isLoadingAccount) {
    return (
      <div className="container mx-auto px-4 py-6 flex justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6 max-w-2xl">
      {!account ? (
        <Button 
          className="w-full"
          onClick={handleConnectStripe}
          disabled={isLoading}
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
          ) : (
            <Plus className="h-4 w-4 mr-2" />
          )}
          {isLoading ? "Setting up..." : "Connect Payment Account"}
        </Button>
      ) : (
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <h3 className="font-medium">Payment Account</h3>
              </div>
              {account.onboarding_completed ? (
                <div className="flex items-center gap-2 text-green-600">
                  <CheckCircle className="h-5 w-5" />
                  <span className="text-sm">Active</span>
                </div>
              ) : (
                <Button 
                  onClick={handleConnectStripe}
                  disabled={isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    "Complete Setup"
                  )}
                </Button>
              )}
            </div>
            
            {account.onboarding_completed ? (
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  Your payment account is set up and ready to receive payouts.
                </p>
                {!account.payouts_enabled && (
                  <div className="flex items-center gap-2 text-yellow-600 bg-yellow-50 p-3 rounded-lg">
                    <XCircle className="h-5 w-5" />
                    <p className="text-sm">
                      Payouts are currently disabled. Please check your Stripe dashboard for more information.
                    </p>
                  </div>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">
                Please complete your account setup to start receiving payouts.
              </p>
            )}
          </CardContent>
        </Card>
      )}

      {account && (
        <Card>
          <CardContent className="p-6">
            <div className="text-sm space-y-2">
              <p className="font-medium">About Payouts</p>
              <p className="text-muted-foreground">
                Payouts are automatically processed after the platform's holding period.
                You can view your earnings and upcoming payouts in the Business section.
              </p>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SellerPayment;
